import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import {
  Box, Typography, IconButton, Tooltip, Chip, CircularProgress, TextField, InputAdornment,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useDispatch, useSelector } from 'react-redux';
import SearchIcon from '@material-ui/icons/Search';
import RoomIcon from '@material-ui/icons/Room';
import FlagIcon from '@material-ui/icons/Flag';
import DirectionsIcon from '@material-ui/icons/Directions';
import FlashOnIcon from '@material-ui/icons/FlashOn';
import FolderOpenIcon from '@material-ui/icons/FolderOpen';
import RefreshIcon from '@material-ui/icons/Refresh';
import { fetchRouteDetail, clearRoute, selectCurrentRoute } from '../../store/slices/routesSlice';
import { setShowDetails, setSidebarTab } from '../../store/slices/uiSlice';
import { AuthContext } from '../../App';
import { buildRouteLegs, buildMonitoringRouteSummary } from '../../utils/buildRouteLegs';

const API_BASE = '/planner-api';

const STATUS_CFG = {
  draft:       { label: 'Rascunho',  color: '#94a3b8', bg: 'rgba(148,163,184,0.12)' },
  planned:     { label: 'Planeada',  color: '#3b82f6', bg: 'rgba(59,130,246,0.12)' },
  in_progress: { label: 'Em Trânsito', color: '#f59e0b', bg: 'rgba(245,158,11,0.12)' },
  completed:   { label: 'Concluída', color: '#22c55e', bg: 'rgba(34,197,94,0.12)' },
  cancelled:   { label: 'Cancelada', color: '#ef4444', bg: 'rgba(239,68,68,0.12)' },
};

const PERIOD_OPTIONS = [
  { key: 'all',   label: 'Todos' },
  { key: 'today', label: 'Hoje' },
  { key: 'week',  label: 'Semana' },
  { key: 'month', label: 'Mês' },
];

const STATUS_OPTIONS = ['draft', 'planned', 'in_progress', 'completed']; // 'cancelled' intentionally excluded — cancelling an OT resets the route to draft

const useStyles = makeStyles(() => ({
  root: {
    padding: '10px 12px',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 4px 6px',
  },
  headerIcon: {
    width: 26, height: 26, borderRadius: 8,
    background: 'linear-gradient(135deg, #f59e0b, #d97706)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    boxShadow: '0 2px 6px rgba(245,158,11,0.3)',
  },
  headerTitle: {
    fontSize: '0.78rem', fontWeight: 700, color: '#0f172a', flex: 1,
  },
  headerHint: {
    fontSize: '0.58rem', color: '#94a3b8', fontWeight: 500,
  },
  searchField: {
    '& .MuiInputBase-root': {
      fontSize: '0.74rem', height: 34, borderRadius: 10,
      background: '#f8fafc', border: 'none',
    },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0,0,0,0.08)' },
  },
  filterRow: {
    display: 'flex',
    gap: 4,
    flexWrap: 'wrap',
  },
  chip: {
    height: 22,
    fontSize: '0.6rem',
    fontWeight: 700,
    cursor: 'pointer',
    border: '1px solid rgba(0,0,0,0.06)',
    background: '#fff',
    color: '#64748b',
  },
  chipActive: {
    background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
    color: '#fff',
    border: '1px solid #2563eb',
  },
  divider: {
    height: 1,
    background: 'linear-gradient(90deg, transparent, rgba(0,0,0,0.06), transparent)',
    margin: '2px 0',
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    minHeight: 100,
  },
  card: {
    background: '#fff',
    borderRadius: 10,
    border: '1px solid rgba(0,0,0,0.06)',
    padding: '8px 10px',
    transition: 'all 0.15s',
    cursor: 'pointer',
    '&:hover': {
      borderColor: 'rgba(59,130,246,0.4)',
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 12px rgba(59,130,246,0.10)',
    },
  },
  cardActive: {
    borderLeft: '3px solid #3b82f6',
    background: 'rgba(59,130,246,0.04)',
  },
  cardHead: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    marginBottom: 4,
  },
  cardName: {
    fontSize: '0.74rem',
    fontWeight: 700,
    color: '#0f172a',
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  statusDot: {
    width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
  },
  point: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    fontSize: '0.66rem',
    color: '#64748b',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  metaRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    marginTop: 4,
    paddingTop: 4,
    borderTop: '1px dashed rgba(0,0,0,0.06)',
  },
  metaText: {
    fontSize: '0.6rem',
    color: '#94a3b8',
    fontWeight: 600,
  },
  metaPrimary: {
    fontSize: '0.62rem',
    color: '#3b82f6',
    fontWeight: 700,
  },
  loadAction: {
    marginLeft: 'auto',
    width: 24, height: 24, borderRadius: 6,
    background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: '#fff',
    transition: 'transform 0.15s',
    '&:hover': { transform: 'scale(1.1)' },
  },
  empty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px 8px',
    gap: 6,
    color: '#94a3b8',
  },
  loadingBox: {
    display: 'flex',
    justifyContent: 'center',
    padding: 20,
  },
}));

function timeAgo(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return 'agora';
  if (min < 60) return `há ${min}m`;
  const h = Math.floor(min / 60);
  if (h < 24) return `há ${h}h`;
  const days = Math.floor(h / 24);
  if (days < 7) return `há ${days}d`;
  return d.toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit' });
}

function formatKm(m) {
  if (!m) return null;
  return `${(m / 1000).toFixed(0)} km`;
}

function periodToDates(period) {
  if (period === 'all') return {};
  const now = new Date();
  const to = now.toISOString();
  const from = new Date(now);
  if (period === 'today') from.setHours(0, 0, 0, 0);
  else if (period === 'week') from.setDate(now.getDate() - 7);
  else if (period === 'month') from.setMonth(now.getMonth() - 1);
  return { dateFrom: from.toISOString(), dateTo: to };
}

function SavedRoutesQuickPanel() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { token } = useContext(AuthContext);
  const currentRoute = useSelector(selectCurrentRoute);

  const [search, setSearch] = useState('');
  const [period, setPeriod] = useState('all');
  const [statusFilter, setStatusFilter] = useState('');
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const searchTimerRef = useRef(null);

  const fetchRoutes = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: 1, limit: 12, sortBy: 'createdAt', sortDir: 'desc' });
      if (search.trim()) params.set('search', search.trim());
      if (statusFilter) params.set('status', statusFilter);
      const dates = periodToDates(period);
      if (dates.dateFrom) params.set('dateFrom', dates.dateFrom);
      if (dates.dateTo) params.set('dateTo', dates.dateTo);
      const res = await fetch(`${API_BASE}/routes?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        const list = data.data || [];
        setRoutes(list);
        setTotal(data.pagination?.total ?? list.length);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('Quick routes fetch failed', err);
    } finally {
      setLoading(false);
    }
  }, [token, search, period, statusFilter]);

  useEffect(() => {
    fetchRoutes();
  }, [fetchRoutes]);

  const handleSearchChange = useCallback((e) => {
    const v = e.target.value;
    setSearch(v);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => fetchRoutes(), 350);
  }, [fetchRoutes]);

  const handleLoadRoute = useCallback(async (r) => {
    const id = r.id || r.dbId;
    if (!token || !id) return;

    // Limpa o estado inspect-mode da rota ANTERIOR antes do clearRoute
    // para o painel inferior não piscar com dados da rota anterior
    // durante a janela de carregamento da nova.
    window.__inspectingRoute = false;
    window.__monitoringOT = null;
    window.__monitoringRouteLegs = null;

    dispatch(clearRoute());
    dispatch(setShowDetails(true));
    dispatch(setSidebarTab(0));

    // Buscamos a rota uma única vez via thunk (já popula Redux) e em
    // paralelo só as OTs. Antes estávamos a buscar a rota DUAS vezes
    // (thunk + fetch directo), o que somava ~300ms ao carregamento e
    // atrasava o primeiro highlight no mapa.
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [routeRes, otsRes] = await Promise.all([
        dispatch(fetchRouteDetail({ token, routeId: id })).unwrap(),
        fetch(`${API_BASE}/transport-orders?assignedRouteId=${id}&pageSize=200`, { headers })
          .then(r => r.ok ? r.json() : null),
      ]);
      if (!routeRes) return;
      const otList = Array.isArray(otsRes)
        ? otsRes
        : (otsRes?.data || otsRes?.items || otsRes?.orders || []);
      if (otList.length === 0) return;  // rota sem OTs → fica em planeamento puro (multi-cor)

      const { routeLegs, orderedOTIds } = buildRouteLegs(routeRes, otList);
      const synthetic = buildMonitoringRouteSummary(routeRes, otList[0]);

      // Flag dedicada — distinta de __fromOTScreen (que dispararia o
      // botão "Voltar às Ordens" indevidamente, já que o operador NÃO
      // veio do ecrã das OTs). O gate do painel inferior aceita
      // qualquer das duas — ver alterações em AppLayout.
      window.__inspectingRoute = true;
      window.__monitoringOT = synthetic;
      window.__monitoringRouteLegs = routeLegs;
      window.dispatchEvent(new CustomEvent('highlightOTLeg', {
        detail: { orderId: null, orderedOTIds, routeLegs },
      }));
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('fitRouteBounds'));
      }, 600);
    } catch (e) {
      // Silencioso: se falhar, o utilizador ainda vê a rota carregada
      // (sem painel inferior, mas funcional). Não vale a pena ruído.
    }
  }, [dispatch, token]);

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Box className={classes.headerIcon}>
          <FlashOnIcon style={{ fontSize: 16, color: '#fff' }} />
        </Box>
        <Box style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Typography className={classes.headerTitle}>Rotas Gravadas</Typography>
          <Chip
            label={total}
            size="small"
            style={{
              height: 20, fontSize: '0.66rem', fontWeight: 700,
              background: 'linear-gradient(135deg, #f59e0b, #d97706)',
              color: '#fff', minWidth: 28,
            }}
          />
        </Box>
        <Tooltip title="Actualizar">
          <IconButton size="small" onClick={fetchRoutes} style={{ padding: 4 }}>
            <RefreshIcon style={{ fontSize: 16, color: '#64748b' }} />
          </IconButton>
        </Tooltip>
      </Box>

      <TextField
        variant="outlined"
        size="small"
        placeholder="Pesquisar matrícula, motorista, nome…"
        value={search}
        onChange={handleSearchChange}
        className={classes.searchField}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon style={{ fontSize: 16, color: '#94a3b8' }} />
            </InputAdornment>
          ),
        }}
      />

      {/* Date picker — feeds the same search box with the formatted date so
          the existing name-based search picks up route names that include
          "· DD/MM/YY". A common operator workflow ("show me what's planned
          for Thursday") becomes a 1-click filter instead of typing. */}
      <Box style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        <Typography style={{ fontSize: '0.62rem', fontWeight: 700, color: '#64748b', letterSpacing: '0.02em' }}>
          Data:
        </Typography>
        <input
          type="date"
          onChange={(e) => {
            const v = e.target.value;
            if (!v) { setSearch(''); fetchRoutes(); return; }
            const parts = v.split('-'); // YYYY-MM-DD
            const formatted = `${parts[2]}/${parts[1]}/${parts[0].slice(2)}`;
            setSearch(formatted);
            fetchRoutes();
          }}
          style={{
            flex: 1, padding: '6px 8px', borderRadius: 6, border: '1px solid #e2e8f0',
            fontSize: '0.7rem', color: '#0f172a', outline: 'none', fontFamily: 'inherit',
          }}
        />
        {search && (
          <Tooltip title="Limpar">
            <IconButton size="small" onClick={() => { setSearch(''); fetchRoutes(); }} style={{ padding: 4 }}>
              <span style={{ fontSize: 12, color: '#94a3b8' }}>✕</span>
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Box className={classes.filterRow}>
        {PERIOD_OPTIONS.map(p => (
          <Chip
            key={p.key}
            label={p.label}
            size="small"
            className={`${classes.chip} ${period === p.key ? classes.chipActive : ''}`}
            onClick={() => setPeriod(p.key)}
          />
        ))}
      </Box>

      <Box className={classes.filterRow}>
        {STATUS_OPTIONS.map(s => {
          const cfg = STATUS_CFG[s];
          const active = statusFilter === s;
          return (
            <Chip
              key={s}
              label={cfg.label}
              size="small"
              className={classes.chip}
              onClick={() => setStatusFilter(active ? '' : s)}
              style={active ? {
                background: cfg.color,
                color: '#fff',
                border: `1px solid ${cfg.color}`,
                boxShadow: `0 2px 6px ${cfg.bg}`,
              } : {
                background: cfg.bg,
                color: cfg.color,
                border: `1px solid ${cfg.color}40`,
              }}
            />
          );
        })}
      </Box>

      <Box className={classes.divider} />

      {loading ? (
        <Box className={classes.loadingBox}>
          <CircularProgress size={22} style={{ color: '#3b82f6' }} />
        </Box>
      ) : routes.length === 0 ? (
        <Box className={classes.empty}>
          <FolderOpenIcon style={{ fontSize: 32, color: '#cbd5e1' }} />
          <Typography style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 500 }}>
            Nenhuma rota encontrada
          </Typography>
          <Typography style={{ fontSize: '0.62rem', color: '#94a3b8' }}>
            Tente alterar os filtros
          </Typography>
        </Box>
      ) : (
        <Box className={classes.list}>
          {routes.map(r => {
            const rid = r.id || r.dbId;
            const status = r.status || r.dbStatus || 'draft';
            const cfg = STATUS_CFG[status] || STATUS_CFG.draft;
            const isActive = currentRoute.computedRoute && currentRoute.dbId === rid;
            const km = formatKm(r.totalDistanceM || r.computedRoute?.distance);
            const ago = timeAgo(r.createdAt || r.updatedAt || r.savedAt);
            return (
              <Box
                key={rid}
                className={`${classes.card} ${isActive ? classes.cardActive : ''}`}
                onClick={() => handleLoadRoute(r)}
              >
                <Box className={classes.cardHead}>
                  <span className={classes.statusDot} style={{ background: cfg.color }} />
                  {/* Primary label: customRef of the linked OT(s) — what
                      the operator typed when creating the OT — concatenated
                      with the service date so the sidebar reads
                      "REF 2026_001 · 07/05/26". When customRef is missing,
                      falls back to the auto-generated route name. */}
                  {(() => {
                    // Primary line = route's auto-generated name (matrícula
                    // · data · N ordens). The customRef + serviceDate of
                    // the linked OT(s) sits below as a subtitle so the
                    // operator scans by route identity first and reads
                    // the OT reference for cross-checking.
                    const titleText = r.name || `Rota #${rid}`;
                    return (
                      <Typography className={classes.cardName} title={titleText}>
                        {titleText}
                      </Typography>
                    );
                  })()}
                  <Box className={classes.loadAction} title="Carregar no mapa">
                    <DirectionsIcon style={{ fontSize: 14 }} />
                  </Box>
                </Box>
                {/* Subtitle = OT's customRef (+ serviceDate) so the
                    operator still sees the OT identifier without it
                    competing visually with the route name. */}
                {r.primaryRef && (() => {
                  const dateStr = r.primaryServiceDate
                    ? new Date(r.primaryServiceDate).toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit', year: '2-digit' })
                    : null;
                  const sub = dateStr ? `${r.primaryRef} · ${dateStr}` : r.primaryRef;
                  return (
                    <Typography style={{ fontSize: '0.6rem', color: '#475569', fontWeight: 600, marginTop: -1, marginBottom: 2, paddingLeft: 14 }}>
                      {sub}
                    </Typography>
                  );
                })()}
                <Box className={classes.point}>
                  <RoomIcon style={{ fontSize: 11, color: '#22c55e', flexShrink: 0 }} />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {r.origin?.name || '—'}
                  </span>
                </Box>
                {/* Intermediate stops (waypoints) — show count when there
                    are any, since listing every name would overflow the
                    sidebar card. */}
                {r.routeDataSummary?.intermediateCount > 0 && (
                  <Box className={classes.point} style={{ color: '#7c3aed', fontWeight: 600 }}>
                    <span style={{ fontSize: 11, flexShrink: 0 }}>↻</span>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {r.routeDataSummary.intermediateCount}{' '}
                      {r.routeDataSummary.intermediateCount === 1
                        ? 'paragem intermédia'
                        : 'paragens intermédias'}
                    </span>
                  </Box>
                )}
                <Box className={classes.point}>
                  <FlagIcon style={{ fontSize: 11, color: '#ef4444', flexShrink: 0 }} />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {r.destination?.name || '—'}
                  </span>
                </Box>
                <Box className={classes.metaRow}>
                  {km && <Typography className={classes.metaPrimary}>{km}</Typography>}
                  <Typography className={classes.metaText} style={{ color: cfg.color }}>
                    · {cfg.label}
                  </Typography>
                  {r.progress?.totalOTs > 0 && (
                    <Typography className={classes.metaText} style={{ color: '#475569' }}>
                      · {r.progress.totalOTs} {r.progress.totalOTs === 1 ? 'ordem' : 'ordens'}
                    </Typography>
                  )}
                  {ago && <Typography className={classes.metaText} style={{ marginLeft: 'auto' }}>{ago}</Typography>}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}

export default SavedRoutesQuickPanel;
